import { useUser } from "@clerk/clerk-react";
import { limit } from "@giftforward/entitlements";
import { PersonAddAlt1Outlined } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useState } from "react";
import { fetchUsers } from "@/api/users";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter, FilterField } from "@/components/BlotterFilterBar";
import InviteUserDialog from "@/components/usersOrgs/InviteUserDialog";
import OrgUserDeleteDialog from "@/components/usersOrgs/OrgUserDeleteDialog";
import OrgUserEditDialog from "@/components/usersOrgs/OrgUserEditDialog";
import OrgUserRowActions, { isSelf, YouBadge } from "@/components/usersOrgs/OrgUserRowActions";
import SeatLimitDialog from "@/components/usersOrgs/SeatLimitDialog";
import { useOrgEntitlements } from "@/hooks/useOrgEntitlements";
import { usePermissions } from "@/hooks/usePermissions";
import { useRoles } from "@/hooks/useRoles";
import { useUsers } from "@/hooks/useUsers";
import type { Organization } from "@/interfaces/organization";
import type { User } from "@/interfaces/user";

const STATUS_OPTIONS: FilterField["options"] = [
  { value: "Enabled", label: "Enabled" },
  { value: "Invited", label: "Invited" },
  { value: "Archived", label: "Archived" },
];

function statusOf(u: User): string {
  if (u.archived) return "Archived";
  if (u.verified) return "Enabled";
  return u.clerkInvitationId ? "Invited" : "Enabled";
}

export default function OrgUsersPanel({ org }: { org: Organization }) {
  const { can } = usePermissions();
  const { roles } = useRoles();
  const { user: currentClerkUser } = useUser();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [seatLimitOpen, setSeatLimitOpen] = useState(false);
  // Org → Users manages the ORG-level membership (u.orgRole); only org-level roles assignable.
  const orgRoles = roles.filter((r) => r.level === "org");

  // Admin-only management: the one capability CLIENT_ADMIN/GFG_SUPER_ADMIN hold that
  // Manager/View Only/GFG_STANDARD_ADMIN don't — see OrgUserRowActions for the same gate
  // applied to the row actions.
  const canManage = can("delete", "USER");

  // Active + invited seat count for THIS org (not the paginated blotter query).
  const { data: seatCountData } = useUsers({ organizationId: org.id, limit: 1 });
  const seatCount = seatCountData?.total ?? 0;

  // The entitlements of the org being VIEWED, not the viewer's. GFG staff reach this panel for
  // arbitrary client orgs through Users & Orgs, so resolving from the caller would answer with
  // GFG's own unlimited plan and the limit would never bind. null is unlimited.
  const { ent } = useOrgEntitlements(org.id);
  const seatLimit = ent ? limit(ent, "users", "max") : null;
  // Until the matrix resolves the limit is unknown, and unknown must not read as unlimited: the
  // invite dialog would open on an organization already at its cap, and the server would refuse
  // the invitation with a 402 after the admin had filled the form in. Inverse of the builder's
  // failure — there an unresolved plan locks too much, here it would unlock too much — and the
  // same rule settles both: never let a request that has not landed grant anything.
  const seatsUnknown = !ent;

  function handleAddUserClick() {
    if (seatsUnknown) return;
    if (!org.isGfg && seatLimit !== null && seatCount >= seatLimit) {
      setSeatLimitOpen(true);
      return;
    }
    setInviteOpen(true);
  }

  /** Blotter data hook, pinned to this org — server-paginated via useUsers. */
  const useOrgUsersData = (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<User> => {
    const valuesFor = (id: string) => q.filters.find((f) => f.fieldId === id)?.values ?? [];
    const { data, isLoading } = useUsers({
      organizationId: org.id,
      search: q.search || undefined,
      roles: valuesFor("role"),
      statuses: valuesFor("status"),
      sortBy: q.sortBy,
      sortDir: q.sortDir,
      page: q.page,
      limit: q.limit,
    });
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  const config: BlotterConfig<User> = {
    title: "Users",
    searchPlaceholder: "Search users in this organization",
    itemNoun: "user",
    minWidth: 720,
    pageSize: 10,
    rowKey: (u) => u.id,
    // Add User stays visible on Starter/Pro regardless of seat usage (still a CTA, just
    // opens the seat-limit modal instead) — only hidden entirely for roles that can't
    // manage users at all.
    headerAction: canManage ? (
      <IconButton
        onClick={handleAddUserClick}
        disabled={seatsUnknown}
        title="Invite user"
        aria-label="Invite user"
        sx={{
          bgcolor: "var(--color-rodeo-black)",
          color: "var(--color-white)",
          borderRadius: "var(--radius-btn)",
          width: 48,
          height: 48,
          "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
        }}
      >
        <PersonAddAlt1Outlined />
      </IconButton>
    ) : undefined,
    filterFields: [
      { id: "status", label: "Status", options: STATUS_OPTIONS },
      {
        id: "role",
        label: "Org Role",
        options: orgRoles.map((r) => ({ value: r.key, label: r.label })),
      },
    ],
    exportFilename: "organization-users.csv",
    exportFetch: async (q) => {
      const res = await fetchUsers({
        organizationId: org.id,
        search: q.search || undefined,
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    useData: useOrgUsersData,
    columns: [
      {
        key: "name",
        label: "Name",
        sortable: true,
        sortValue: (u) => u.name,
        render: (u) => (
          <span style={{ fontWeight: 600 }}>
            {u.name}
            {isSelf(u, currentClerkUser?.id) && <YouBadge />}
          </span>
        ),
      },
      {
        key: "email",
        label: "Email",
        sortable: true,
        sortValue: (u) => u.email,
        render: (u) => <span style={{ color: "var(--color-graphite)" }}>{u.email}</span>,
      },
      {
        key: "orgRole",
        label: "Org Role",
        render: (u) => orgRoles.find((r) => r.key === u.orgRole)?.label ?? u.orgRole ?? "—",
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        sortValue: (u) => statusOf(u),
        render: (u) => statusOf(u),
      },
      {
        key: "actions",
        label: "",
        render: (u) => <OrgUserRowActions user={u} onEdit={setEditUser} onDelete={setDeleteUser} />,
      },
    ],
  };

  return (
    <>
      <Blotter config={config} />
      {/*
       * fixedOrg locks the Organization field to this org and passes its id to the
       * API payload. The backend's scopeWriteGuard independently enforces the same
       * restriction, so a Client Admin cannot bypass this by manipulating the request.
       */}
      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        fixedOrg={{ id: org.id, name: org.name }}
      />
      <OrgUserEditDialog user={editUser} org={org} onClose={() => setEditUser(null)} />
      <OrgUserDeleteDialog user={deleteUser} onClose={() => setDeleteUser(null)} />
      <SeatLimitDialog
        open={seatLimitOpen}
        plan={org.plan}
        onClose={() => setSeatLimitOpen(false)}
      />
    </>
  );
}
